import {
    select as D3select,
    sum as D3sum,
    group as D3group,
} from 'd3';
import { get } from 'lodash';

// random utility functions needed by several d3 components

// generic falsy test
const IsEmpty = function (object) {
    if (!object ||
        object == null ||
        object === '' ||
        object === undefined ||
        object === 0 ||
        typeof object === 'undefined') {
        return true;
    }

    if (Object.keys(object).length === 0 && object.constructor === Object) {
        return true;
    }

    if (typeof object === 'object' && object != null) {
        return object.length !== undefined && object.length === 0;
    }

    // e.g., 5 is not empty
    return typeof object === 'object';
};

// ok to shove some code in here?
const GetContrastText = function (color, theme) {
    // http://www.w3.org/TR/AERT#color-contrast
    const bigint = parseInt((color || theme.palette.secondary.main).substring(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    const brightness = Math.round(((parseInt(r) * 299) +
                      (parseInt(g) * 587) +
                      (parseInt(b) * 114)) / 1000);
    return (brightness > 125) ? theme.palette.common.black : theme.palette.common.white;
};

// prevent other handlers from getting called on ctrl-click (context menu)
// It ends up eating all subsequent event clicks on a Mac
const LetContextMenu = function (e) {
    if (e.button || e.ctrlKey) {
        e.stopImmediatePropagation();
    }
};

// turn a list of user agents to a hierarchy graph
const Hierarchify = function (bkts, f) {
    const m = { k: 'All', v: 0, c: {} };
    // best effort to parse app, version and the remaining junk
    const parseUA = function (ss) {
        let app;
        let ver;
        let rest;
        let vers;
        let i = ss.indexOf('/');
        if (i > 0) {
            app = ss.substring(0, i);
            const j = ss.indexOf(' ', i);
            if (j > 0) {
                ver = ss.substring(i + 1, j);
                rest = ss.substring(j + 1);
                vers = ss.substring(0, j);
            } else {
                ver = ss.substring(i + 1);
                vers = ss;
            }
        } else {
            i = ss.indexOf(' ');
            if (i > 0) {
                app = ss.substring(0, i);
                rest = ss.substring(i + 1);
            } else {
                app = ss;
            }
        }
        return { app, ver: ver || '', vers: vers || '', rest: rest || '' };
    };
    bkts.forEach(d => {
        const ua = parseUA(d.key);
        let n = m.c[ua.app] = m.c[ua.app] || { k: ua.app, v: 0, s: ua.app, c: {}, ua: d.key };
        if (ua.ver.length > 0) {
            n = n.c[ua.ver] = n.c[ua.ver] || { k: ua.ver, v: 0, s: ua.vers, c: {}, ua: d.key };
        }
        if (ua.rest.length > 0) {
            n = n.c[ua.rest] = n.c[ua.rest] || { k: ua.rest, v: 0, s: ua.s, c: {}, ua: d.key };
        }
        n.v = f(d); // .doc_count
        n.s = d.key;
    });
    // sum up
    const sum = function (n) {
        return !Object.keys(n.c).length ? n.v : D3sum(Object.keys(n.c), c => sum(n.c[c]));
    };
    // convert to something D3hierarchy understands
    const convert = from => {
        return {
            name: from.k,
            sum: sum(from),
            size: from.v,
            str: from.s,
            ua: from.ua, // this is the original user agent string
            children: Object.keys(from.c).map(k => convert(from.c[k]))
        };
    };
    return convert(m);
};

// merges old geo data into new map
const MergeNodes = function (o, n) {
    // merge locations
    Object.entries(o.locations || {}).forEach(([k, v]) => {
        if (!IsEmpty(n.locations[k])) {
            v.count.actual += n.locations[k].count.actual;
            v.count.known += n.locations[k].count.known;
            Object.entries(v.nodes).forEach(([nk, nv]) => {
                const nn = n.locations[k].nodes[nk] || { actual: 0, known: 0 };
                v.nodes[nk].actual += nn.actual;
                v.nodes[nk].known += nn.known;
            });
        }
        n.locations[k] = v;
    });
    // merge traffic
    Object.entries(o.traffic || {}).forEach(([k, v]) => {
        if (!IsEmpty(n.traffic[k])) {
            v.count += n.traffic[k].count;
            v.direction |= n.traffic[k].direction;
            Object.entries(v.connections).forEach(([ok, ov]) => {
                const nn = n.traffic[k].connections[ok] || { direction: 0, count: 0 };
                v.connections[ok].count += nn.count;
                v.connections[ok].direction += nn.direction;
            });
        }
        n.traffic[k] = v;
    });
    n.initial = o.initial;
    return n;
};

// To place the element myRect relative to refRect withing bounds
// Doesn't modify inputs.
// Used for placing modals next to the clicked element and whatnots
// TODO: needs a param that provides a control structure with pad and offset if fits properly
const EltPlacement = function (refRect, myRect, bounds, xoffset) {
    const pad = 1;
    const vLim = bounds.h - pad;
    const hLim = bounds.w - pad;
    // initially try to attach by bottom left
    // probably need a separate 'offset' param if fit
    // precisely
    let newT = refRect.bottom + pad;
    let newL = refRect.left;
    const newB = newT + myRect.height;
    let newR = newL + myRect.width;
    // vertical fuckery - need to scoot up and to the right
    if (newB > vLim) {
        newT -= (newB - vLim);
        // above movement has caused myRect overlap with refRect vertically
        // try to move myRect to the right side of refRect.
        // to the left, to the left - everything you own in a box to the left
        newL = refRect.right + xoffset + pad;
        newR = newL + myRect.width;
    }
    // vertical fuckery - need to scoot left
    if (newR > hLim) { // too far to the right - get back to the screen
        newL -= newR - hLim;
        if (newT < refRect.top) { // overlap again - go to the left of refElt
            newL = refRect.left - myRect.width - pad;
        }
    }

    // doesn't fit - go to the top left
    if (newL < 0) newL = 0;
    if (newT < 0) newT = 0;

    return { l: newL, t: newT };
};

const NestMetrics = function (info, history, ignore) {
    const ret = [];
    const nest = D3group(history, d => Object.keys(d[info.name]));

    const objects = {};
    nest.forEach(function (value, keys) {
        keys.forEach(function (a) {
            if (a) {
                objects[a] = a;
            }
        });
    });

    (ignore || []).forEach(i => delete objects[i]);

    Object.keys(objects).forEach(function (o, i) {
        ret.push({
            index: i,
            timeline: history,
            key: o,
            metrics: info.metrics.map(function (m, j) {
                const nm = Object.assign({}, m);
                nm.key = o + '.' + m.key;
                nm.value = function (d) { return get(d, info.name + '.' + this.key) };
                // we only need to display the object name once
                nm.title = j === 0 ? o : '';
                nm.subtitle = m.title;
                return nm;
            })
        });
    });
    return ret;
};

const lineHeight = 1.3; // ems
function AddValueLine (elmt, val, i, theme) {
    const dy = parseFloat(elmt.attr('dy') || 0);
    const tspan = elmt.append('tspan')
        .attr('x', elmt.attr('x') || 0)
        .attr('y', elmt.attr('y'))
        .attr('dy', i * lineHeight + dy + 'em');

    (val.length > 0) && tspan.append('tspan')
        .attr('class', 'tip-texta')
        .attr('text-anchor', 'left')
        .text(val.length > 0 ? val[0] : '');

    (val.length > 1) && tspan.append('tspan')
        .attr('class', 'tip-div')
        .attr('text-anchor', 'middle')
        .text(val.length > 1 ? '\u00A0 | \u00A0' : '');

    (val.length > 1) && tspan.append('tspan')
        .attr('class', 'tip-textb')
        .attr('text-anchor', 'middle')
        .style('font', theme.typography.tooltips.font)
        .style('fill', theme.palette.info.light)
        .style('color', theme.palette.info.light)
        .text(val.length > 1 ? val[1] : '');

    (val.length > 2) && tspan.append('tspan')
        .attr('class', 'tip-div')
        .attr('text-anchor', 'middle')
        .text(val.length > 2 ? '\u00A0 | \u00A0' : '');
    (val.length > 2) && tspan.append('tspan')
        .attr('class', 'tip-textc')
        .attr('text-anchor', 'middle')
        .style('font', theme.typography.tooltips.font)
        .style('fill', theme.palette.info.light)
        .style('color', theme.palette.info.light)
        .text(val.length > 2 ? val[2] : '');
}

const MoveToFront = (elmt) => {
    return D3select(elmt).each(function () {
        this.parentNode.appendChild(this);
    });
};

export {
    IsEmpty,
    LetContextMenu,
    Hierarchify,
    MergeNodes,
    EltPlacement,
    NestMetrics,
    GetContrastText,
    AddValueLine,
    MoveToFront,
};
